
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useInviteCollaborator } from "@/hooks/useCollaboration";
import { CollaborationPermission } from "@/types/collaboration";
import { getPermissionLabel } from "@/utils/collaboration";

interface InviteCollaboratorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  testId: string;
}

export const InviteCollaboratorDialog = ({ open, onOpenChange, testId }: InviteCollaboratorDialogProps) => {
  const [email, setEmail] = useState("");
  const [permission, setPermission] = useState<CollaborationPermission>("view_responses");
  const inviteCollaborator = useInviteCollaborator();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) return;

    try {
      await inviteCollaborator.mutateAsync({
        testId,
        email: email.trim(),
        permission,
      });
      
      // Reset form and close dialog
      setEmail("");
      setPermission("view_responses");
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite Collaborator</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="permission">Permission Level</Label>
            <Select value={permission} onValueChange={(value: CollaborationPermission) => setPermission(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select permission level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="view_responses">
                  {getPermissionLabel("view_responses")} - Can view test responses only
                </SelectItem>
                <SelectItem value="manage_recruitment">
                  {getPermissionLabel("manage_recruitment")} - Can view responses and change recruitment status
                </SelectItem>
                <SelectItem value="full_access">
                  {getPermissionLabel("full_access")} - Can do everything except delete the test
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={inviteCollaborator.isPending}>
              {inviteCollaborator.isPending ? "Sending..." : "Send Invitation"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
